import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { timesheetApi } from '../services/api'
import Button from '../components/common/Button'
import toast from 'react-hot-toast'

export default function SketchList() {
    const { uuid } = useParams()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const referenceNo = searchParams.get('referenceNo')

    const [drawings, setDrawings] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        loadDrawings()
    }, [referenceNo])

    async function loadDrawings() {
        if (!referenceNo) {
            setLoading(false)
            return
        }
        try {
            const response = await timesheetApi.getDrawings(referenceNo)
            if (response.success) {
                setDrawings(response.drawings || [])
            }
        } catch (error) {
            console.error('Error loading drawings:', error)
        }
        setLoading(false)
    }

    async function handleDelete(drawingId, title) {
        if (!confirm(`Are you sure you want to delete "${title || 'this drawing'}"?`)) return

        try {
            const response = await timesheetApi.deleteDrawing(drawingId)
            if (response.success) {
                toast.success('Drawing deleted')
                loadDrawings()
            } else {
                toast.error(response.message || 'Failed to delete')
            }
        } catch (error) {
            toast.error('Error deleting drawing')
        }
    }

    function handleNew() {
        navigate(`/timesheet/${uuid}/sketch?referenceNo=${encodeURIComponent(referenceNo)}`)
    }

    function handleEdit(drawingId) {
        navigate(`/timesheet/${uuid}/sketch?referenceNo=${encodeURIComponent(referenceNo)}&drawingId=${drawingId}`)
    }

    function handleBack() {
        navigate(`/timesheet/${uuid}`)
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner w-8 h-8"></div>
            </div>
        )
    }

    return (
        <div>
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Sketches</h1>
                    {referenceNo && (
                        <p className="text-blue-100 mt-1">Ref: {referenceNo}</p>
                    )}
                </div>
                <div className="flex gap-2">
                    <Button onClick={handleNew}>+ New Sketch</Button>
                    <Button variant="secondary" onClick={handleBack}>← Back</Button>
                </div>
            </div>

            {drawings.length === 0 ? (
                <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
                    <p className="text-4xl mb-4">🎨</p>
                    <p className="text-lg font-medium">No sketches yet</p>
                    <p className="mt-2">Tap "New Sketch" to create one</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {drawings.map((drawing) => (
                        <div
                            key={drawing.id}
                            className="bg-white rounded-xl shadow-md overflow-hidden"
                        >
                            {/* Thumbnail */}
                            <div
                                className="h-48 bg-gray-50 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => handleEdit(drawing.id)}
                            >
                                {drawing.drawingData ? (
                                    <img
                                        src={drawing.drawingData}
                                        alt={drawing.drawingTitle}
                                        className="w-full h-full object-contain"
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-full text-gray-400">
                                        No preview
                                    </div>
                                )}
                            </div>

                            {/* Info */}
                            <div className="p-4 flex items-center justify-between">
                                <div>
                                    <p className="font-semibold text-gray-800">
                                        {drawing.drawingTitle || 'Untitled'}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        {drawing.createdAt ? new Date(drawing.createdAt).toLocaleString() : ''}
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleEdit(drawing.id)}
                                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(drawing.id, drawing.drawingTitle)}
                                        className="text-red-500 hover:text-red-700 text-sm font-medium"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}